<template>
    <div class="main-wrapper">
        <div class="container">

            <v-overlay :value="dataLoading">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
            </v-overlay>

            <h1 class="title">{{ companyName || "" }}</h1>

            <div class="stepper-header" v-if="step < 6">
                <div @click="handleStepperClick(1)"
                     class="stepper-step"
                     :class="{ complete: highestStepVisited > 1, active: step === 1 }"
                     :style="(highestStepVisited >= 1 && step <= 5) ? 'cursor: pointer;' : ''"
                >
                    <span class="step-number">1</span>
                    <span class="step-label">Car</span>
                </div>
                <div class="step-divider" :class="{ active: step >= 2 }"></div>
                <div @click="handleStepperClick(2)"
                     class="stepper-step"
                     :class="{ active: step === 2, complete: highestStepVisited > 2 }"
                     :style="(highestStepVisited >= 2 && step <= 5) ? 'cursor: pointer;' : ''"
                >
                    <span class="step-number">2</span>
                    <span class="step-label">Shipment</span>
                </div>
                <div class="step-divider" :class="{ active: step >= 3 }"></div>
                <div @click="handleStepperClick(3)"
                     class="stepper-step"
                     :class="{ active: step === 3, complete: highestStepVisited > 3 }"
                     :style="(highestStepVisited >= 3 && step <= 5) ? 'cursor: pointer;' : ''"
                >
                    <span class="step-number">3</span>
                    <span class="step-label">Date</span>
                </div>
                <div class="step-divider" :class="{ active: step >= 4 }"></div>
                <div @click="handleStepperClick(4)"
                     class="stepper-step"
                     :class="{ active: step === 4, complete: highestStepVisited > 4 }"
                     :style="(highestStepVisited >= 4 && step <= 5) ? 'cursor: pointer;' : ''"
                >
                    <span class="step-number">4</span>
                    <span class="step-label">Comment</span>
                </div>
            </div>

            <div class="stepper-content">
                <div v-if="step === 1" class="content-card">

                    <label class="input-label">Your vehicle registration number</label>
                    <div class="input-wrapper">
                        <i class="fal fa-truck-moving input-icon"></i>
                        <input type="text"
                               v-model="formData.carNumber"
                               placeholder="123 ABC"
                               style="text-transform: uppercase;"
                        />
                    </div>

                    <button
                            :disabled="!formData.carNumber"
                            class="ml-auto"
                            :class="['navigation-btn', { 'disabled-btn': !formData.carNumber }]"
                            @click="nextStep"
                    >
                        Next
                    </button>
                </div>

                <div v-if="step === 2" class="content-card">

                    <label class="input-label">Choose shipment type</label>
                    <div class="option-group">
                        <button
                                class="option-btn"
                                :class="{ selected: formData.type === 'truck' }"
                                @click="setFormData('type', 'truck')"
                        >
                            <i class="fal fa-truck-moving option-icon"></i>
                            Truck
                            <i class="fal fa-check checkmark" v-if="formData.type === 'truck'"></i>
                        </button>
                        <button
                                class="option-btn"
                                :class="{ selected: formData.type === 'container' }"
                                @click="setFormData('type', 'container')"
                        >
                            <i class="fal fa-truck-container option-icon"></i>
                            Container
                            <i class="fal fa-check checkmark" v-if="formData.type === 'container'"></i>
                        </button>
                    </div>

                    <label class="input-label" v-if="formData.type === 'container'">Choose container size</label>
                    <div class="option-group" v-if="formData.type === 'container'">
                        <button
                                class="option-btn"
                                :class="{ selected: formData.size === '20_feet' }"
                                @click="setFormData('size', '20_feet')"
                        >
                            20 feet
                            <i class="fal fa-check checkmark" v-if="formData.size === '20_feet'"></i>
                        </button>
                        <button
                                class="option-btn"
                                :class="{ selected: formData.size === '40_feet' }"
                                @click="setFormData('size', '40_feet')"
                        >
                            40 feet
                            <i class="fal fa-check checkmark" v-if="formData.size === '40_feet'"></i>
                        </button>
                    </div>

                    <label class="input-label" v-if="formData.type === 'container'">Is the shipment with or without
                        pallets?</label>
                    <div class="option-group" v-if="formData.type === 'container'">
                        <button
                                class="option-btn"
                                :class="{ selected: formData.withPallets === 'with_pallets' }"
                                @click="setFormData('withPallets', 'with_pallets')"
                        >
                            With pallets
                            <i class="fal fa-check checkmark" v-if="formData.withPallets === 'with_pallets'"></i>
                        </button>
                        <button
                                class="option-btn"
                                :class="{ selected: formData.withPallets === 'without_pallets' }"
                                @click="setFormData('withPallets', 'without_pallets')"
                        >
                            Without pallets
                            <i class="fal fa-check checkmark" v-if="formData.withPallets === 'without_pallets'"></i>
                        </button>
                    </div>

                    <label class="input-label" v-if="formData.type === 'truck'">Number of pallets</label>
                    <div class="option-group" v-if="formData.type === 'truck'">
                        <button
                                class="option-btn"
                                :class="{ selected: formData.pallets === '1_19' }"
                                @click="setFormData('pallets', '1_19')"
                        >
                            1–19
                            <i class="fal fa-check checkmark" v-if="formData.pallets  === '1_19'"></i>
                        </button>
                        <button
                                class="option-btn"
                                :class="{ selected: formData.pallets === '20' }"
                                @click="setFormData('pallets', '20')"
                        >
                            20-32
                            <i class="fal fa-check checkmark" v-if="formData.pallets === '20'"></i>
                        </button>
                        <button
                                class="option-btn"
                                :class="{ selected: formData.pallets === '33' }"
                                @click="setFormData('pallets', '33')"
                        >
                            33+
                            <i class="fal fa-check checkmark" v-if="formData.pallets === '33'"></i>
                        </button>
                    </div>
                    <div style="display: flex;justify-content: space-between;gap: 12px">

                        <button @click="prevStep" class="navigation-btn">
                            Back
                        </button>
                        <button
                                :disabled="isStep2NextDisabled"
                                :class="['navigation-btn', { 'disabled-btn': isStep2NextDisabled }]"
                                @click="nextStep"
                        >
                            Next
                        </button>
                    </div>
                </div>

                <div v-if="step === 3" class="content-card calendar-card">
                    <div class="sticky-marker"></div>
                    <vuecal
                            :key="vuecalKey"
                            :events="computedShipments"
                            :disable-views="disabledViews"
                            hide-view-selector
                            show-week-numbers
                            hide-weekends
                            :snapToTime="snapToTime"
                            :time-from="timeFrom"
                            :time-to="timeToDisplay"
                            :time-step="30"
                            :time-cell-height="60"
                            :min-date="minDate"
                            :cell-click-hold="false"
                            :drag-to-create-event="false"
                            :editable-events="{ title: false, drag: true, resize: false, delete: true, create: false }"
                            :disable-days=disabledDates
                            ref="vuecal"
                            @cell-click="onEventCreate"
                            @ready="onReady"
                            @event-delete="onEventDelete"
                            @event-drop="onEventDrop"
                    >
                        <template #event="{ event, view }">
                            <div v-if="view === 'week' || view === 'day'" class="event-content">
                                <span class="timerange-span">
                                  {{ event.start.formatTime("HH:mm") }} - {{ event.end.formatTime("HH:mm") }}
                                </span>
                                <div
                                        class="reserved-container"
                                        :class="event?.class === 'new-event' ? 'new-event' : 'locked'"
                                >
                                    <i
                                            class="reserved-icon"
                                            :class="event?.class === 'new-event' ? 'far fa-plus' : 'far fa-lock-alt'"
                                    ></i>
                                    <span class="reserved-span">
                                        {{ event?.class === 'new-event' ? 'New booking' : 'Booked' }}
                                    </span>
                                </div>
                            </div>
                        </template>

                        <template #title="view">
                            {{ view.title }}
                        </template>
                    </vuecal>
                    <div class="nav-btn-div">

                        <button @click="prevStep" class="navigation-btn">
                            Back
                        </button>

                        <button
                                :disabled="!createdEvent"
                                :class="['navigation-btn', { 'disabled-btn': !createdEvent }]"
                                @click="nextStep"
                        >
                            Next
                        </button>
                    </div>
                </div>

                <div v-if="step === 4" class="content-card">
                    <template v-if="submitLoading">
                        <div class="loader-wrapper">
                            <v-progress-circular
                                    :size="70"
                                    :width="3"
                                    color="rgba(216, 35, 50, 1)"
                                    indeterminate
                            ></v-progress-circular>
                        </div>
                    </template>
                    <template v-else>
                        <label class="input-label">Your email</label>
                        <div class="input-wrapper" :class="{ 'input-error': emailError }">
                            <i class="fal fa-envelope input-icon"></i>
                            <input type="text"
                                   v-model="formData.email"
                                   placeholder="email@website.com"
                                   @blur="validateEmail"
                            />
                        </div>
                        <label class="input-label">Add a comment <span style="font-weight: 400">(optional)</span>
                        </label>
                        <div class="input-wrapper">
                            <textarea v-model="formData.comment" placeholder="Comment" rows="4"></textarea>
                        </div>
                        <div style="display: flex;justify-content: space-between;gap: 12px">
                            <button @click="prevStep" class="navigation-btn">
                                Back
                            </button>
                            <button :class="['navigation-btn', { 'disabled-btn': emailError || !formData.email }]"
                                    @click="finalStep"
                                    :disabled="!formData.email || emailError"
                            >
                                Confirm
                            </button>
                        </div>
                    </template>
                </div>

                <div v-if="step === 5" class="content-card">
                    <div class="summary-header">
                        <div>
                            <i class="fal fa-check-circle summary-icon"></i>
                        </div>
                        <span class="summary-title">Loading time reserved!</span>
                        <span class="summary-label">Here is your booking summary:</span>
                    </div>
                    <div class="summary-divider"></div>
                    <div class="summary-content">
                        <div class="summary-grid">
                            <div class="summary-labels">
                                <div class="summary-label">Vehicle reg num:</div>
                                <div class="summary-label">Shipment:</div>
                                <div class="summary-label">Email:</div>
                                <div class="summary-label">Date:</div>
                                <div class="summary-label">Time:</div>
                            </div>
                            <div class="summary-values">
                                <div class="summary-value">{{ formData.carNumber.toUpperCase() }}</div>
                                <div class="summary-value">{{ getShipmentSizeDetail('text') }}</div>
                                <div class="summary-value">{{ formData.email }}</div>
                                <div class="summary-value">{{ formattedEventDate }}</div>
                                <div class="summary-value">{{ formattedEventTimeRange }}</div>
                            </div>
                        </div>
                        <div class="summary-comment" v-if="formData.comment">
                            <div class="summary-label">Comment:</div>
                            <div class="summary-value">
                                {{ formData.comment }}
                            </div>
                        </div>
                    </div>
                    <div style="display: flex;justify-content: space-between;gap: 12px">
                        <button @click="handleEditClick" class="navigation-btn ml-auto">
                            Edit
                        </button>
                    </div>
                </div>

                <div v-if="step === 6 && savedData" class="content-card">
                    <div class="summary-header">
                        <div>
                            <i class="fal fa-check-circle summary-icon"></i>
                        </div>
                        <span class="summary-title">Loading time reserved!</span>
                        <span class="summary-label">Here is your booking summary:</span>
                    </div>
                    <div class="summary-divider"></div>
                    <div class="summary-content">
                        <div class="summary-grid">
                            <div class="summary-labels">
                                <div class="summary-label">Vehicle reg num:</div>
                                <div class="summary-label">Shipment:</div>
                                <div class="summary-label">Email:</div>
                                <div class="summary-label">Date:</div>
                                <div class="summary-label">Time:</div>
                            </div>
                            <div class="summary-values">
                                <div class="summary-value">{{ savedData.car_nr.toUpperCase() || "-" }}</div>
                                <div class="summary-value">{{
                                        getDetailForSize(savedData.shipment_size_id, 'text') || "-"
                                    }}
                                </div>
                                <div class="summary-value">{{ savedData.ext_creator_email || "-" }}</div>
                                <div class="summary-value">{{ savedData.shipment_date || "-" }}</div>
                                <div class="summary-value">{{ savedData.shipment_time_range || "-" }}</div>
                            </div>
                        </div>
                        <div class="summary-comment" v-if="savedData.comment">
                            <div class="summary-label">Comment:</div>
                            <div class="summary-value">
                                {{ savedData.comment }}
                            </div>
                        </div>
                        <div style="display: flex;justify-content: space-between;gap: 12px">
                            <button @click="handleEditClick" class="navigation-btn ml-auto">
                                Edit
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import VueCal from 'vue-cal'
import { helperMixin } from "../../../mixins/helper-mixin";
import holidays from '../../../pages/shipment/tab-items/data-item/public-holidays.json'
import moment from "moment";
import { mapMutations, mapState, mapActions } from "vuex";

export default {
    mixins: [helperMixin],
    components: { vuecal: VueCal },
    data() {
        return {
            step: 1,
            highestStepVisited: 1,
            formData: {
                carNumber: '',
                type: null,
                size: null,
                withPallets: null,
                pallets: null,
                comment: null,
                email: null,
            },
            vuecalKey: 0,
            snapToTime: 15,
            timeFrom: 480,
            timeTo: 930,
            timeToDisplay: 960,
            events: [],
            isMobile: window.innerWidth < 600,
            emailError: false,
            eventCreatedFromSavedData: false,
            rerenderCalendar: false,
        };
    },
    watch: {
        'formData.type'(newType) {
            if (newType === 'truck') {
                this.formData.size = null;
                this.formData.withPallets = null;
            } else if (newType === 'container') {
                this.formData.pallets = null;
            }
        },
        disabledViews() {
            this.vuecalKey++
        },
        shipmentStatus() {
            if (this.shipmentStatus === "waiting") this.step = 6
        },
        savedData: {
            handler(newVal) {
                if (newVal) {
                    this.prefillSavedData();
                    this.handleRedirectFromEmail();
                }
            },
            immediate: true
        }
    },
    mounted() {
        window.addEventListener('resize', this.handleResize)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize)
    },
    computed: {
        ...mapState("PublicShipmentStore", ["sizes", "companyName", "shipments", "direction", "createdEvent", "dataLoading",
            "submitLoading", "savedData", "shipmentStatus"]),
        disabledDates() {
            return Object.values(holidays).flatMap(year => Object.values(year));
        },
        minDate() {
            return new Date()
        },
        isStep2NextDisabled() {
            if (!this.formData.carNumber) return true;

            if (this.formData.type === 'truck') {
                return !this.formData.pallets;
            }

            if (this.formData.type === 'container') {
                return !(this.formData.size && this.formData.withPallets);
            }

            return true;
        },
        computedShipments() {
            return [...this.shipments, ...(this.createdEvent ? [this.createdEvent] : [])]
        },
        localEventStart() {
            return this.createdEvent ? new Date(this.createdEvent?.start) : null
        },
        localEventEnd() {
            return this.createdEvent ? new Date(this.createdEvent.end) : null
        },
        formattedEventDate() {
            if (!this.localEventStart) return ''
            return this.localEventStart.toLocaleDateString('et-EE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })
        },
        formattedEventTimeRange() {
            if (!this.localEventStart || !this.localEventEnd) return ''
            const opts = { hour: '2-digit', minute: '2-digit' }
            return `${ this.localEventStart.toLocaleTimeString('et-EE', opts) }–${ this.localEventEnd.toLocaleTimeString('et-EE', opts) }`
        },
        disabledViews() {
            return this.isMobile
                    ? ['years', 'year', 'week', 'month']
                    : ['years', 'year', 'day', 'month'];
        }
    },
    methods: {
        ...mapMutations("PublicShipmentStore", ["setCreatedEvent", "setRequestError", "setErrorMessage"]),
        ...mapActions("PublicShipmentStore", ["submitEvent"]),
        prevStep() {
            if (this.step > 0) {
                this.step--;
            }
        },
        nextStep() {
            if (this.step < 5) {
                this.saveProgress(false).then(success => {
                    if (success) {
                        this.step++;
                        this.highestStepVisited = Math.max(this.highestStepVisited, this.step);
                    }
                });
            }
        },
        finalStep() {
            this.saveProgress(true).then(success => {
                if (success) {
                    this.step++;
                    this.highestStepVisited = Math.max(this.highestStepVisited, this.step);
                }
            });
        },
        saveProgress(finalStep) {
            if (!(this.createdEvent?.end || !this.createdEvent?.start) && finalStep) {
                this.setRequestError(true);
                this.setErrorMessage("Please, select a time slot");
                return Promise.resolve(false);
            }

            const payload = {
                token: this.$route.params.token,
                shipment_size_id: this.getShipmentSizeDetail('id'),
                shipment_size_text: this.getShipmentSizeDetail('text'),
                car_nr: this.formData.carNumber,
                comment: this.formData.comment,
                start: this.createdEvent?.start.format('YYYY-MM-DD HH:mm'),
                end: this.createdEvent?.end.format('YYYY-MM-DD HH:mm'),
                email: this.formData.email,
                final_step: finalStep
            };
            return this.submitEvent(payload);
        },
        async onEventCreate(eventDatetime, ignoreTimeCheck = false) {
            await this.deleteEvent()
            this.setRequestError(false)
            this.setErrorMessage("")

            let momentTime = moment(eventDatetime, 'YYYY-MM-DD HH:mm');
            const snapTo = this.snapToTime || 15;
            const minutes = momentTime.minute();
            const roundedMinutes = Math.round(minutes / snapTo) * snapTo;
            momentTime = momentTime.minute(roundedMinutes).second(0)

            const sizeValue = this.getShipmentSizeDetail('value');
            const isFullDay = sizeValue === 480;

            if (isFullDay) {
                momentTime.set({
                    hour: Math.floor(this.timeFrom / 60),
                    minute: this.timeFrom % 60,
                    second: 0,
                    millisecond: 0
                });
            }

            const now = moment();

            if (!ignoreTimeCheck && momentTime.isBefore(now)) {
                this.setRequestError(true)
                this.setErrorMessage("Please, select a future time slot")
                return false;
            }

            const weekdayIndex = momentTime.isoWeekday();
            const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
            await delay(1);
            const availableTimeSlots = await this.getAvailableTimeSlots(weekdayIndex);

            const check = this.beforeCreateCheck(momentTime, sizeValue, availableTimeSlots)

            if (!check.success) {
                this.setRequestError(true)

                const msg = isFullDay
                        ? "Whole day is not available for scheduling"
                        : check.reason === 'after-hours'
                                ? "Selected time exceeds the latest allowed time (15:30)"
                                : "Selected time will overlap with an existing event";

                this.setErrorMessage(msg)
                return false;
            }

            const createdEvent = this.$refs.vuecal.createEvent(
                    momentTime.format('YYYY-MM-DD HH:mm'),
                    sizeValue,
                    {
                        title: this.companyName,
                        content: this.formData.carNumber,
                        class: 'new-event'
                    }
            );
            this.setCreatedEvent(createdEvent)
            return true
        },
        getShipmentSizeDetail(detailType) {
            if (this.formData.type === 'truck') {
                if (this.formData.pallets === '33') return this.getDetailForSize('truck_33_pallets', detailType);
                if (this.formData.pallets === '20') return this.getDetailForSize('truck_20_pallets', detailType);
                if (this.formData.pallets === '1_19') return this.getDetailForSize('1_19_pallets', detailType);
                return this.getDetailForSize('1_19_pallets', detailType);
            }

            if (this.formData.type === 'container') {
                const sizePart = this.formData.size === '20_feet' ? '20' : '40';
                const pelletPart = this.formData.withPallets === 'with_pallets' ? 'with_pallets' : 'without_pallets';
                const id = `${ sizePart }_foot_container_${ pelletPart }`;
                return this.getDetailForSize(id, detailType);
            }

            return null;
        },
        getDetailForSize(id, detailType) {
            const direction = this.direction;
            const item = this.sizes[direction].find(x => x.id === id);

            if (!item) return null;

            if (detailType === 'id') return item.id;
            if (detailType === 'text') return item.text;
            if (detailType === 'value') return item.value;

            return null;
        },
        async getAvailableTimeSlots(weekdayIndex) {
            const weekday = this.isMobile ? this.$refs.vuecal.view : this.$refs.vuecal.$children[weekdayIndex];
            if (!weekday) return {};

            const availableSlots = {};
            for (let time = this.timeFrom; time <= this.timeTo; time += this.snapToTime) {
                availableSlots[time] = 1;
            }

            weekday.events.forEach(event => {
                if (event.class === "new-event") return
                const start = event.startTimeMinutes;
                const end = event.endTimeMinutes;

                for (let time = start; time < end; time += this.snapToTime) {
                    if (availableSlots[time] !== undefined) {
                        availableSlots[time] = 0;
                    }
                }
            });

            return availableSlots;
        },

        beforeCreateCheck(momentTime, sizeValue, availableTimeSlots) {
            const startTimeMinutes = momentTime.hour() * 60 + momentTime.minute();
            const durationInMinutes = sizeValue;
            const wholeDay = this.timeTo - this.timeFrom < durationInMinutes;
            const endTimeMinutes = startTimeMinutes + durationInMinutes;

            if (wholeDay) {
                const isFree = Object.values(availableTimeSlots).every(slot => slot === 1);
                return { success: isFree, reason: isFree ? null : 'overlap' };
            }

            if (endTimeMinutes > this.timeTo) {
                return { success: false, reason: 'after-hours' };
            }

            for (let time = startTimeMinutes; time < endTimeMinutes; time += this.snapToTime) {
                if (availableTimeSlots[time] === 0) {
                    return { success: false, reason: 'overlap' };
                }
            }

            return { success: true, reason: null };
        },

        async deleteEvent() {
            const currentView = this.$refs.vuecal.view.startDate
            this.setCreatedEvent(null)
            this.vuecalKey++
            this.$nextTick(() => {
                const fallbackView = this.disabledViews.includes('week') ? 'day' : 'week'
                this.$refs.vuecal.switchView(fallbackView, currentView)
            })
        },
        async onEventDrop(event) {
            const updatedEvent = event.event
            const allEvents = this.$refs.vuecal.view.events

            const now = new Date()
            now.setSeconds(0, 0)
            const snapToMs = this.snapToTime * 60 * 1000
            const minAllowedTime = new Date(Math.floor(now.getTime() / snapToMs) * snapToMs)

            const totalDuration = updatedEvent.endTimeMinutes - updatedEvent.startTimeMinutes
            const isFullDay = totalDuration === 480

            if (isFullDay) {
                updatedEvent.start = new Date(updatedEvent.start)
                updatedEvent.start.setHours(Math.floor(this.timeFrom / 60), this.timeFrom % 60, 0, 0)

                updatedEvent.end = new Date(updatedEvent.start)
                updatedEvent.end.setMinutes(updatedEvent.start.getMinutes() + 480)
            }

            const isInPast = new Date(updatedEvent.start) < minAllowedTime
            const endTimeLaterThanDayEnd = isFullDay ? false : updatedEvent.endTimeMinutes > this.timeTo

            const hasOverlap = allEvents.some(ev => {
                if (ev._eid === updatedEvent._eid) return false
                return (
                        new Date(ev.start) < new Date(updatedEvent.end) &&
                        new Date(updatedEvent.start) < new Date(ev.end)
                )
            })

            const isValid = !hasOverlap && !isInPast && !endTimeLaterThanDayEnd

            if (isValid) {
                this.createdEvent.start = updatedEvent.start
                this.createdEvent.end = updatedEvent.end
                this.vuecalKey++
            } else {
                this.vuecalKey++
                let msg = "Please, select a free time slot"
                if (isInPast) msg = "Start time must be in the future"
                else if (endTimeLaterThanDayEnd) msg = "End time exceeds allowed limit (15:30)"
                else if (hasOverlap) msg = "Time slot overlaps with another event"
                this.setErrorMessage(msg)
                this.setRequestError(true)
            }
        },
        onReady() {
            this.replacePrevNextIcons();
            this.removeClickableFromCalendarWeekdays();
            this.observeStickyHeader();
            if (this.savedData && this.savedData.arrival_at && !this.eventCreatedFromSavedData) {
                this.eventCreatedFromSavedData = true;
                this.$nextTick(async () => {
                    await this.onEventCreate(this.savedData.arrival_at, true);
                    this.setCalendarViewToCurrentTime()
                });
            } else {
                this.setCalendarViewToCurrentTime()
            }
            if (this.rerenderCalendar) {
                this.setCreatedEvent(null)
                this.rerenderCalendar = false;
                this.vuecalKey++;
            }
        },
        onEventDelete(e) {
            this.setCreatedEvent(null)
        },
        replacePrevNextIcons() {
            const nextArrow = document.querySelector('.vuecal__arrow--next');
            const prevArrow = document.querySelector('.vuecal__arrow--prev');
            if (nextArrow) nextArrow.innerHTML = '<i class="fal fa-chevron-right"></i>';
            if (prevArrow) prevArrow.innerHTML = '<i class="fal fa-chevron-left"></i>';
        },
        removeClickableFromCalendarWeekdays() {
            const headers = document.querySelectorAll('.vuecal__heading.clickable');

            headers.forEach(header => {
                header.addEventListener('click', e => {
                    e.stopImmediatePropagation();
                    e.preventDefault();
                }, true);
            });
        },
        handleResize() {
            const mobile = window.innerWidth < 600
            if (mobile !== this.isMobile) {
                this.isMobile = mobile
            }
        },
        validateEmail() {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            this.emailError = !re.test(this.formData.email)
        },
        async prefillSavedData() {
            const savedData = { ...this.savedData };
            let targetStep = 1;

            this.formData.carNumber = savedData.car_nr;
            this.formData.email = savedData.ext_creator_email;
            this.formData.comment = savedData.comment;

            const id = savedData.shipment_size_id;

            if (id) {
                if (id.startsWith('truck')) {
                    this.formData.type = 'truck';
                    if (id === 'truck_33_pallets') this.formData.pallets = '33';
                    else if (id === 'truck_20_pallets') this.formData.pallets = '20';
                    else this.formData.pallets = '1_19';
                    this.formData.size = null;
                    this.formData.withPallets = null;
                } else if (id.includes('container')) {
                    this.formData.type = 'container';
                    this.formData.size = id.includes('20_foot') ? '20_feet' : '40_feet';
                    this.formData.withPallets = id.includes('with_pallets') ? 'with_pallets' : 'without_pallets';
                    this.formData.pallets = null;
                }
                targetStep = 2;
            }

            if (savedData.arrival_at) targetStep = 3;
            if (this.shipmentStatus === "waiting") targetStep = 6;

            this.step = targetStep;
            this.highestStepVisited = targetStep;
        },
        handleEditClick() {
            this.step = 3
            this.highestStepVisited = 5
        },
        handleRedirectFromEmail() {
            const url = new URL(window.location.href)
            const editParam = url.searchParams.get('edit')

            if (editParam === '1') {
                this.handleEditClick?.()
                url.searchParams.delete('edit')
                window.history.replaceState({}, '', url.toString())
            }
        },
        handleStepperClick(step) {
            if (step > this.highestStepVisited) return
            this.step = step
        },
        observeStickyHeader() {
            const marker = document.querySelector('.sticky-marker');
            const calendarCard = document.querySelector('.calendar-card');
            if (!marker || !calendarCard) return;

            const observer = new IntersectionObserver(
                    ([entry]) => {
                        calendarCard.classList.toggle('sticky-active', entry.intersectionRatio < 1);
                    },
                    { threshold: [1] }
            );

            observer.observe(marker);
        },
        setCalendarViewToCurrentTime() {
            const fallbackView = this.disabledViews.includes('week') ? 'day' : 'week'
            setTimeout(async () => {
                if (!this.createdEvent?.start) return
                this.$refs.vuecal.switchView(fallbackView, this.createdEvent.start);
            }, 100);
        },
        setFormData(field, value) {
            this.formData[field] = value
            this.rerenderCalendar = true;
        }
    }
};
</script>

<style scoped>
.main-wrapper {
    min-height: 100vh;
    background: #f0f0f0;
    padding-top: 40px;
    display: flex;
    justify-content: center;
}

.container {
    width: 100%;
    padding: 0 16px;
}

@media (min-width: 600px) and (max-width: 1020px) {
    .calendar-card {
        margin-left: 12px;
        margin-right: 12px;
    }
}

@media (min-width: 600px) {
    .container {
        padding: 0;
    }

    .content-card {
        max-width: 500px;
    }

    .calendar-card {
        max-width: 1000px !important;
    }

    .navigation-btn {
        width: 160px !important;
    }

    ::v-deep(.vuecal__title-bar) {
        background-color: white;
        padding-bottom: 12px !important;
    }

    ::v-deep(.vuecal__header) {
        z-index: 100;
        background-color: white;
        position: sticky !important;
        top: 0;
    }
}

@media (max-width: 600px) {
    .calendar-card.sticky-active ::v-deep(.vuecal__title-bar) {
        padding-top: 24px;
        border-bottom: 1px solid rgb(227 227 227);
    }
}

.calendar-card.sticky-active ::v-deep(.vuecal__title-bar) {
    padding-top: 24px;
}

.title {
    font-size: 24px;
    font-weight: bold;
    text-align: center;
    margin-bottom: 20px;
}

.stepper-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
}

.stepper-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
}

.step-number {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 1px solid rgba(198, 198, 198, 1);
    color: rgba(141, 153, 146, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 700;
}

.stepper-step.active .step-number {
    border: 1px solid rgba(216, 35, 50, 1) !important;
    background: #fff !important;
    color: rgba(216, 35, 50, 1) !important;
}

.stepper-step.complete .step-number {
    border: none;
    background: rgba(216, 35, 50, 1);
    color: #fff;
}

.step-label {
    font-size: 14px;
    margin-top: 8px;
    text-align: center;
    color: #000;
}

.step-divider {
    flex: 1;
    height: 1px;
    background: rgba(198, 198, 198, 1);
    margin: 0 4px 24px;
}

.step-divider.active {
    background: rgba(216, 35, 50, 1);
}

.stepper-content {
    display: flex;
    justify-content: center;
}

.content-card {
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 8px;
    padding: 24px;
    width: 100%;
    gap: 16px;
}

.input-label {
    font-size: 16px;
    display: block;
    font-weight: 600;
    line-height: 100%;
    color: rgba(57, 57, 57, 1);
}

.input-wrapper {
    display: flex;
    align-items: center;
    border: 1px solid rgba(223, 226, 224, 1);
    border-radius: 8px;
    padding: 12px;
    gap: 12px;
}

.input-icon {
    font-size: 20px;
}

.input-wrapper input {
    border: none;
    outline: none;
    flex: 1;
    line-height: 18px;
}

.input-wrapper input::placeholder {
    line-height: 18px;
    color: rgba(141, 153, 146, 1);
}

.input-wrapper textarea {
    border: none;
    outline: none;
    flex: 1;
    line-height: 18px;
    resize: vertical;
    font-family: inherit;
}

.input-wrapper textarea::placeholder {
    color: rgba(141, 153, 146, 1);
    line-height: 18px;
}

.navigation-btn {
    width: 100%;
    height: 44px;
    border-radius: 8px;
    padding: 7px 16px;
    gap: 12px;
    background: rgba(216, 35, 50, 1);
    color: #fff;
    border: none;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.checkmark {
    color: rgba(216, 35, 50, 1);
    font-size: 16px;
}

.option-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 8px;
    padding: 12px;
    gap: 12px;
    background: rgba(240, 240, 240, 1);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.option-btn.selected {
    background: rgba(244, 193, 198, 1);
}

.option-group {
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
}

.option-icon {
    font-size: 20px;
    color: rgba(100, 100, 100, 1);
}

::v-deep(.vuecal__event.existing-event) {
    border-radius: 8px;
    background: rgba(240, 240, 240, 1);
    padding: 12px;
    text-align: left;
}

::v-deep(.vuecal__event.new-event) {
    border-radius: 8px;
    background: rgba(244, 193, 198, 1);
    padding: 12px;
    text-align: left;
}

::v-deep(.vuecal) {
    box-shadow: none;
}

::v-deep(.vuecal__time-column .vuecal__time-cell-line:before) {
    left: 42px
}

::v-deep(.vuecal__time-column .vuecal__time-cell) {
    text-align: left;
    font-size: 14px;
    font-weight: 400;
    line-height: 14px;
    letter-spacing: 0;
    color: rgba(141, 153, 146, 1);
}

::v-deep(.vuecal__time-column .vuecal__time-cell) {
    padding-right: 0;
}

::v-deep(.vuecal--view-with-time .vuecal__weekdays-headings) {
    padding-right: 0;
    padding-top: 12px;
}

::v-deep(.vuecal__weekdays-headings) {
    margin-bottom: 0;
    border: none
}

::v-deep(.vuecal .clickable) {
    cursor: default;
}

::v-deep(.vuecal__heading .weekday-label) {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0;
    color: rgba(141, 153, 146, 1);
}

::v-deep(.vuecal__title-bar) {
    background-color: white;
    padding-bottom: 12px;
}

::v-deep(.vuecal__header) {
    background-color: white;
    position: sticky !important;
    z-index: 100;
    top: 0;
}

::v-deep(.vuecal__arrow--next .angle) {
    display: none;
}

::v-deep(.vuecal__arrow--next) {
    margin-right: 0;
}

::v-deep(.vuecal__arrow--prev .angle) {
    display: none;
}

::v-deep(.vuecal__arrow--next),
::v-deep(.vuecal__arrow--prev) {
    width: 42px;
    height: 38px;
    border-radius: 8px;
    border: 1px solid rgba(223, 226, 224, 1);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: 0;
}

::v-deep(.vuecal__arrow--next i),
::v-deep(.vuecal__arrow--prev i) {
    font-size: 20px;
}

::v-deep(.vuecal__arrow--next:hover),
::v-deep(.vuecal__arrow--prev:hover) {
    background-color: rgba(0, 0, 0, 0.03);
}

.reserved-icon {
    color: rgba(100, 100, 100, 1);
    font-size: 20px;
}

.reserved-span {
    font-size: 14px;
    font-weight: 600;
}

.timerange-span {
    font-size: 14px;
    font-weight: 400;
}

.reserved-container {
    padding-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.summary-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.summary-grid {
    display: flex;
    gap: 12px;
}

.summary-labels,
.summary-values {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.summary-value {
    font-size: 14px;
    font-weight: 600;
    color: rgba(57, 57, 57, 1);
}

.summary-comment {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.summary-header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}

.summary-title {
    font-weight: 700;
    line-height: 28px;
    font-size: 24px;
    margin-bottom: 12px;
}

.summary-icon {
    font-size: 50px;
    font-weight: 100 !important;
    color: #D52A2A;
    margin-bottom: 24px;
}

.summary-divider {
    height: 1px;
    background-color: rgba(223, 226, 224, 1);
    margin-left: -24px;
    margin-right: -24px;
}

.summary-label {
    font-size: 14px;
}

.disabled-btn {
    background-color: rgba(240, 240, 240, 1);
    color: rgba(141, 153, 146, 1);
    cursor: not-allowed;
}

.nav-btn-div {
    display: flex;
    justify-content: space-between;
    gap: 12px
}

.loader-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
}

.input-error {
    border: 1px solid red;
}
</style>